import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import {
  AdminApiError,
  AdminApiForbidden,
  AdminApiNetworkError,
} from '../api/AdminApiClient'
import {
  kFakeOperatorEnabled, kFakeOperatorRole, kGoogleClientId,
} from '../config'

// LoginPage handles three flows:
// 1. The Google Identity Services (GIS) button mounted into a div
//    inside this page; on credential success the GIS callback hands us
//    the Google ID token, which we forward to AuthProvider.signIn().
// 2. A dev-only "DEV: continue as <role>" button that POSTs the
//    `fake-operator-<role>` synthetic credential. Only rendered when
//    `kFakeOperatorEnabled` is true; production builds tree-shake the
//    branch and admin/deploy.sh refuses to run with the env set.
// 3. Error rendering for the two backend rejection codes the operator
//    will plausibly hit during onboarding (NOT_AN_OPERATOR) or with a
//    flaky token (INVALID_CREDENTIAL); everything else collapses to
//    "Sign-in failed."

interface GoogleIdAccounts {
  initialize: (config: {
    client_id: string
    callback: (resp: { credential: string }) => void
    use_fedcm_for_prompt?: boolean
  }) => void
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void
}

interface GoogleApi { accounts: { id: GoogleIdAccounts } }

declare global {
  interface Window { google?: GoogleApi }
}

type ErrorState =
  | { kind: 'none' }
  | { kind: 'not-an-operator' }
  | { kind: 'invalid-credential' }
  | { kind: 'generic'; message: string }

function renderError(state: ErrorState) {
  switch (state.kind) {
    case 'none':
      return null
    case 'not-an-operator':
      return (
        <p className="mt-4 rounded-md bg-stone-100 p-3 text-sm text-stone-700">
          Your Google account is not registered as an operator. Contact
          an administrator to be added.
        </p>
      )
    case 'invalid-credential':
      return (
        <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
          Sign-in failed. Please try again.
        </p>
      )
    case 'generic':
      return (
        <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
          {state.message}
        </p>
      )
  }
}

function classifyError(err: unknown): ErrorState {
  if (err instanceof AdminApiForbidden && err.errorCode === 'NOT_AN_OPERATOR') {
    return { kind: 'not-an-operator' }
  }
  if (err instanceof AdminApiError) {
    if (err.errorCode === 'INVALID_CREDENTIAL') {
      return { kind: 'invalid-credential' }
    }
    return { kind: 'generic', message: `Sign-in failed (${err.errorCode || err.status}).` }
  }
  if (err instanceof AdminApiNetworkError) {
    return { kind: 'generic', message: 'Network error. Check your connection.' }
  }
  return { kind: 'generic', message: 'Sign-in failed. Please try again.' }
}

export function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const buttonContainerRef = useRef<HTMLDivElement | null>(null)
  const [error, setError] = useState<ErrorState>({ kind: 'none' })
  const [busy, setBusy] = useState(false)
  const [gisReady, setGisReady] = useState<boolean>(
    typeof window !== 'undefined' && !!window.google,
  )

  // If GIS is not on the page yet (fallback), poll lightly until it is.
  useEffect(() => {
    if (gisReady) return
    const interval = window.setInterval(() => {
      if (window.google) {
        setGisReady(true)
        window.clearInterval(interval)
      }
    }, 100)
    // Give up after ~10s and let the operator refresh.
    const timeout = window.setTimeout(() => window.clearInterval(interval), 10_000)
    return () => {
      window.clearInterval(interval)
      window.clearTimeout(timeout)
    }
  }, [gisReady])

  // Wire the GIS button once it's loaded. Skipped when no client id is
  // configured (only realistic in tests; production deploys require it).
  useEffect(() => {
    if (!gisReady || !kGoogleClientId) return
    const api = window.google?.accounts.id
    if (!api || !buttonContainerRef.current) return
    api.initialize({
      client_id: kGoogleClientId,
      callback: async (resp) => {
        if (!resp?.credential) return
        setBusy(true)
        setError({ kind: 'none' })
        try {
          await auth.signIn(resp.credential)
          navigate('/', { replace: true })
        } catch (e) {
          setError(classifyError(e))
        } finally {
          setBusy(false)
        }
      },
    })
    api.renderButton(buttonContainerRef.current, {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'pill',
    })
  }, [gisReady, auth, navigate])

  const onFakeOperatorClick = async () => {
    if (!kFakeOperatorRole) return
    setBusy(true)
    setError({ kind: 'none' })
    try {
      await auth.signIn(`fake-operator-${kFakeOperatorRole}`)
      navigate('/', { replace: true })
    } catch (e) {
      setError(classifyError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-stone-50 p-6">
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-stone-900">
          Operator sign-in
        </h1>
        <p className="mt-2 text-sm text-stone-600">
          Use your Google account. Access is restricted to registered
          operators.
        </p>

        <div className="mt-6 flex justify-center" data-testid="gis-slot">
          {gisReady
            ? <div ref={buttonContainerRef} />
            : <p className="text-sm text-stone-500">Loading sign-in…</p>}
        </div>

        {kFakeOperatorEnabled && (
          <div className="mt-6 border-t border-stone-200 pt-4">
            <p className="text-xs uppercase tracking-wide text-stone-500">
              Dev shortcut
            </p>
            <button
              type="button"
              onClick={onFakeOperatorClick}
              disabled={busy}
              className="mt-2 w-full rounded-md bg-red-600 px-4 py-2 text-sm
                         font-medium text-white hover:bg-red-700
                         disabled:opacity-50"
            >
              DEV: continue as {kFakeOperatorRole}
            </button>
            <p className="mt-2 text-xs text-stone-500">
              Bypasses Google. Requires ALLOW_FAKE_LOGIN=1 and
              ALLOW_FAKE_OPERATOR=1 on the backend.
            </p>
          </div>
        )}

        {renderError(error)}
      </div>
    </div>
  )
}
